
import { defineComponent, ref, computed, onMounted } from "vue";
import { usePlugin } from "@/plugins";
import {
  QuestionItem,
  UpsertQuestionRequest,
  Question,
  GetQuestionRequest,
  GetQuestionResponse,
  QuestionType,
  QuestionItemType,
} from "@/types";
import QuestionPanel from "@/components/QuestionPanel.vue";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";

export default defineComponent({
  name: "QuestionScreen",
  components: { QuestionPanel },
  setup() {
    const { onBack, setLoading, error, success, axios, route } = usePlugin();

    const newQuestion: Question = {
      id: 0,
      name: "",
      title: "",
      body: "",
      type: QuestionType.Training,
      createDate: null,
      createBy: null,
      updateDate: null,
      updateBy: null,
      createByEmployee: null,
      updateByEmployee: null,
      isoDocumentNo: "",
    };
    const question = ref<Question>(newQuestion);
    const items = ref<QuestionItem[]>([]);

    const rules = computed(() => {
      return {
        question: {
          name: { required },
          title: { required },
          type: { required },
        },
      };
    });
    const v$ = useVuelidate(rules, { question });
    const questionTypes = [
      { code: 1, description: "Text" },
      { code: 2, description: "Scale" },
      { code: 3, description: "Scale with text" },
      { code: 4, description: "Choice" },
    ];
    const showPopup = ref(false);
    const onShowPopup = () => (showPopup.value = true);
    const onNewQuestionItem = () => {
      items.value.push({ item: items.value.length } as QuestionItem);
    };
    const onDeleteQuestionItem = (index: number) => {
      items.value.splice(index, 1);
    };
    const id = computed(() =>
      route.params["id"] ? parseInt(route.params["id"] as string) : null
    );
    const title = computed(() =>
      id.value != null
        ? `แก้ไขแบบประเมิน ID ${id.value}`
        : "สร้างแบบประเมินใหม่"
    );
    const loadDocument = async () => {
      setLoading(true);
      try {
        const result = await axios.post<GetQuestionResponse>(
          "api/v1/services/question/get",
          {
            id: id.value,
            searchString: "",
          } as GetQuestionRequest
        );
        question.value = result.data.questions[0];
        items.value = JSON.parse(question.value.body ?? "[]");
        success("ดึงข้อมูลสำเร็จแล้ว");
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const onSave = async () => {
      v$.value.$touch();
      if (v$.value.$invalid) return;

      if (question.value == undefined)
        throw Error("invalid question object undefined");
      setLoading(true);
      try {
        question.value.body = JSON.stringify(items.value);
        await axios.post("api/v1/services/question/upsert", {
          question: question.value,
        } as UpsertQuestionRequest);
        success("บันทึกข้อมูลสำเร็จแล้ว");
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    onMounted(async () => {
      if (id.value != null) {
        loadDocument();
      } else {
        items.value = [];
      }
    });
    return {
      items,
      onBack,
      onNewQuestionItem,
      onDeleteQuestionItem,
      questionTypes,
      onSave,
      title,
      id,
      question,
      showPopup,
      onShowPopup,
      v$,
      QuestionItemType,
    };
  },
});
